/**
 * Copyright Highway9 Networks Inc.
 *
 * Applies WebSocket CREATE/DELETE/UPDATE patches directly onto RTK EntityState
 * via adapter helpers (upsertMany / removeMany / setMany), so websocket updates
 * no longer require shipping whole collections through the patch worker.
 */
import type { EntityAdapter, EntityId, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { current, isDraft } from "immer";
import jsonmergepatch from "json-merge-patch";
import type { WebSocketMessage } from "~/types/WebsocketMessage";

type EntityWithId = { id?: EntityId };
type PatchValues = NonNullable<WebSocketMessage["modifiedValues"]>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isEmptyPatch = (patchValues: PatchValues) => Object.keys(patchValues).length === 0;

const requiresMergePatch = (value: unknown): boolean => {
  if (value === null) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some(requiresMergePatch);
  }
  if (isRecord(value)) {
    return true;
  }
  return false;
};

const canApplyShallowUpdate = (patch: WebSocketMessage): patch is WebSocketMessage & { modifiedValues: PatchValues } => {
  const values = patch.modifiedValues;
  if (!values || isEmptyPatch(values)) {
    return false;
  }
  if ("id" in values) {
    return false;
  }
  if ((patch.objectType || "").toLowerCase() === "radio" && Array.isArray(values.edgeConfig)) {
    return false;
  }
  return !Object.values(values).some(requiresMergePatch);
};

const cloneEntity = <T,>(entity: T): T => {
  // structuredClone is required: current() returns the FROZEN base object for
  // unmodified drafts (and shares unmodified children otherwise), and
  // jsonmergepatch.apply mutates its target in place
  const plain = isDraft(entity) ? current(entity) : entity;
  return structuredClone(plain);
};

const groupByEventType = (patches: WebSocketMessage[]) => {
  const groups: Partial<Record<string, WebSocketMessage[]>> = {};
  for (const patch of patches) {
    const key = patch.eventType;
    const bucket = groups[key] ?? [];
    bucket.push(patch);
    groups[key] = bucket;
  }
  return groups;
};

/**
 * Preserve radio edgeConfig.name values that the backend may omit from UPDATE
 * patches. Mutates `patch.modifiedValues.edgeConfig` in place (same behavior as
 * the worker implementation in src/sw/workers.ts).
 */
export const preserveRadioEdgeNames = (
  currentObject: Record<string, unknown>,
  patch: WebSocketMessage,
) => {
  if ((patch.objectType || "").toLowerCase() !== "radio" || !patch.modifiedValues) {
    return;
  }

  const edgeConfigPatch = patch.modifiedValues.edgeConfig;
  if (!Array.isArray(edgeConfigPatch)) {
    return;
  }

  const currentEdgeConfig = Array.isArray(currentObject?.edgeConfig)
    ? (currentObject.edgeConfig as Array<{ id?: string; name?: string }>)
    : [];
  const existingNameById = new Map<string, string>();

  for (const edge of currentEdgeConfig) {
    if ( edge &&  typeof edge.id === "string" && edge.id && typeof edge.name === "string" && edge.name ) {
      existingNameById.set(edge.id, edge.name);
    }
  }

  const oldEdgeConfig = Array.isArray(patch.oldValues?.edgeConfig) ? (patch.oldValues?.edgeConfig as Array<{ id?: string; name?: string }>): [];
  for (const edge of oldEdgeConfig) {
    const isEdge = edge && typeof edge.id === "string" && edge.id && typeof edge.name === "string" && edge.name && !existingNameById.has(edge.id);
    if (isEdge && edge.id && edge.name) {
      existingNameById.set(edge.id, edge.name);
    }
  }

  patch.modifiedValues.edgeConfig = edgeConfigPatch.map((edge: unknown) => {
    if (!edge || typeof edge !== "object") {
      return edge;
    }
    const edgeObj = edge as { id?: string; name?: string };
    // Inject the preserved name only when the patch entry omits one - a
    // backend-sent name (rename) must win (matches src/sw/workers.ts)
    if (!edgeObj.name && typeof edgeObj.id === "string" && edgeObj.id && existingNameById.has(edgeObj.id)) {
      return {
        ...edgeObj,
        name: existingNameById.get(edgeObj.id),
      };
    }
    return edge;
  });
};

/**
 * Resolve an entity by EntityState key, falling back to matching `entity.id`
 * when the adapter uses a custom selectId (e.g. system settings keyed by name).
 * The fallback index is built at most once per applySocketPatches call - a
 * per-patch scan is O(patches x entities) on 10k+ collections.
 */
type IdIndex = { map: Map<EntityId, EntityId> | null };

const findEntity = <T extends EntityWithId>(
  adapter: EntityAdapter<T>,
  state: EntityState<T>,
  objectId: string,
  idIndex: IdIndex,
): { entity: T; key: EntityId } | null => {
  const byKey = state.entities[objectId];
  if (byKey) {
    return { entity: byKey, key: objectId };
  }
  if (!idIndex.map) {
    idIndex.map = new Map();
    for (const key of state.ids) {
      const entity = state.entities[key];
      if (entity?.id !== undefined) {
        idIndex.map.set(entity.id, key);
      }
    }
  }
  const key = idIndex.map.get(objectId);
  if (key === undefined) return null;
  const entity = state.entities[key];
  return entity ? { entity, key } : null;
};

/**
 * Apply CREATE → DELETE → UPDATE patches onto an EntityState collection.
 * Missing UPDATE targets are ignored unless `opts.upsertOnMissing` is set
 * (operations: missed CREATE still surfaces later status updates).
 */
export const applySocketPatches = <T extends EntityWithId>(
  adapter: EntityAdapter<T>,
  state: EntityState<T>,
  patches: WebSocketMessage[],
  opts?: { upsertOnMissing?: boolean },
): void => {
  const grouped = groupByEventType(patches);
  const idIndex: IdIndex = { map: null };
  // Batch merges and write once per group: on a sorted adapter every setOne
  // re-sorts the whole collection, so per-patch writes are O(N x D log D)
  // and freeze the tab on large device fleets
  const mergePatch = (base: T, patch: WebSocketMessage): T => {
    const clone = cloneEntity(base) as T & Record<string, unknown>;
    preserveRadioEdgeNames(clone, patch);
    return jsonmergepatch.apply(clone, patch.modifiedValues) as T;
  };

  if (grouped.CREATE?.length) {
    const creates: T[] = [];
    const mergedById = new Map<EntityId, T>();
    for (const patch of grouped.CREATE) {
      if (!patch.modifiedValues || !patch.objectId) continue;
      const found = findEntity(adapter, state, patch.objectId, idIndex);
      if (found) {
        // Existing entity: merge like UPDATE so a partial CREATE does not clobber fields.
        mergedById.set(found.key, mergePatch(mergedById.get(found.key) ?? found.entity, patch));
      } else {
        creates.push({
          id: patch.objectId,
          ...patch.modifiedValues,
        } as T);
      }
    }
    if (mergedById.size) {
      adapter.setMany(state, Array.from(mergedById.values()));
    }
    if (creates.length) {
      adapter.upsertMany(state, creates);
    }
    idIndex.map = null;
  }

  if (grouped.DELETE?.length) {
    const deleteKeys: EntityId[] = [];
    for (const patch of grouped.DELETE) {
      if (!patch.objectId) continue;
      const found = findEntity(adapter, state, patch.objectId, idIndex);
      deleteKeys.push(found?.key ?? patch.objectId);
    }
    if (deleteKeys.length) {
      adapter.removeMany(state, deleteKeys);
    }
    idIndex.map = null;
  }

  if (grouped.UPDATE?.length) {
    const mergedById = new Map<EntityId, T>();
    const upsertsById = new Map<string, T>();
    for (const patch of grouped.UPDATE) {
      if (!patch.modifiedValues || !patch.objectId) continue;
      if (isEmptyPatch(patch.modifiedValues)) continue;
      const found = findEntity(adapter, state, patch.objectId, idIndex);
      if (!found) {
        if (opts?.upsertOnMissing) {
          // Later same-id patches deep-merge onto the pending upsert, matching
          // the sequential apply-then-find behavior
          const pending = upsertsById.get(patch.objectId);
          upsertsById.set(
            patch.objectId,
            pending
              ? (jsonmergepatch.apply(structuredClone(pending), patch.modifiedValues) as T)
              : ({ id: patch.objectId, ...patch.modifiedValues } as T),
          );
        }
        continue;
      }

      // Shallow scalar patches are RFC 7396-equivalent to a spread - skip the
      // deep clone but stay in the batched pipeline so same-id ordering holds
      const base = mergedById.get(found.key) ?? found.entity;
      if (canApplyShallowUpdate(patch)) {
        const plainBase = isDraft(base) ? current(base) : base;
        mergedById.set(found.key, { ...plainBase, ...patch.modifiedValues } as T);
      } else {
        mergedById.set(found.key, mergePatch(base, patch));
      }
    }
    if (mergedById.size) {
      adapter.setMany(state, Array.from(mergedById.values()));
    }
    if (upsertsById.size) {
      adapter.upsertMany(state, Array.from(upsertsById.values()));
    }
  }
};

/**
 * Factory that returns a slice reducer applying websocket patches to a nested
 * EntityState field. Each uniform slice can add it in one line:
 *
 *   applySocketPatches: createSocketPatchReducer(sitesAdapter, (s) => s.sitesData)
 */
export const createSocketPatchReducer = <S, T extends EntityWithId>(
  adapter: EntityAdapter<T>,
  selectSub: (state: S) => EntityState<T>,
  opts?: { upsertOnMissing?: boolean },
) => {
  return (state: S, action: PayloadAction<WebSocketMessage[]>) => {
    // Entity adapter helpers mutate Immer drafts in place inside createSlice.
    applySocketPatches(adapter, selectSub(state) as EntityState<T>, action.payload, opts);
  };
};
